import React, { useRef, useState } from 'react';
import { api } from '../../services/Axios';
import ConfirmationModal from '../utils/avisos/ConfirmationModal';
import MessageModal from '../utils/avisos/MessageModal';
import ErrorDetailModal from '../utils/avisos/ErrorDetailModal';
import '../../styles/Main.css';

const UploadExcel = ({ onUploadSuccess }: { onUploadSuccess: () => void }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [errorDetails, setErrorDetails] = useState<{ row: number; error: string }[]>([]);
  const [warningDetails, setWarningDetails] = useState<{ row: number; warning: string }[]>([]);
  const [detailReport, setDetailReport] = useState<any>(null);
  const [showErrorDetailModal, setShowErrorDetailModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setShowConfirmation(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/api/productos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessageContent(`Archivo cargado exitosamente: ${response.data.successCount} productos cargados.`);
      setErrorDetails(response.data.errors || []);
      setWarningDetails(response.data.warnings || []);
      setDetailReport(response.data.detailReport || null);
      setShowMessage(true);
      setShowErrorDetailModal(response.data.errors.length > 0 || response.data.warnings.length > 0);
      onUploadSuccess();
    } catch (error) {
      setMessageContent('Error al cargar el archivo');
      setShowMessage(true);
    } finally {
      setUploading(false);
      handleRemoveFile(); // Limpiar el archivo seleccionado después de cargar
    }
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
    handleRemoveFile();
  };

  const handleCloseErrorDetailModal = () => {
    setShowErrorDetailModal(false);
  };

  return (
    <div className="upload-container">
      <h3 className="upload-title">Importa tu Inventario desde Excel</h3>
      <p className="upload-instruction">
        Suba un archivo Excel (.xlsx) con la información de los productos. Asegúrese de que el archivo incluya las columnas de 
        <strong> nombre, cantidad, umbral, categoría</strong> y otras propiedades importantes. Puede descargar una plantilla de ejemplo a continuación.
      </p>

      {/* Área de arrastrar y soltar */}
      <div
        className={`file-drop-zone ${isDragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <label htmlFor="fileInput" className="file-upload-btn">
          <i className="fas fa-file-upload"></i> {file ? file.name : "Arrastra un archivo aquí o haz clic para elegir"}
        </label>
        <input
          type="file"
          id="fileInput"
          accept=".xlsx"
          onChange={handleFileChange}
          ref={fileInputRef}
          className="hidden-file-input"
        />
      </div>

      {/* Botón para quitar archivo seleccionado */}
      {file && (
        <button onClick={handleRemoveFile} className="remove-file-btn">
          <i className="fas fa-times-circle"></i> Quitar archivo
        </button>
      )}

      <div className="upload-buttons">
        <button onClick={() => setShowConfirmation(true)} disabled={!file || uploading} className="upload-btn">
          {uploading ? (
            'Cargando...'
          ) : (
            <>
              <i className="fas fa-upload"></i> Subir archivo
            </>
          )}
        </button>
        <a href="/plantilla/Plantilla.xlsx" download className="template-btn">
          <i className="fas fa-file-excel"></i> Descargar plantilla
        </a>
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        title="Confirmación de Carga"
        message="¿Estás seguro de que quieres cargar este archivo?"
        onConfirm={handleUpload}
        onCancel={() => setShowConfirmation(false)}
      />

      <MessageModal
        isOpen={showMessage}
        message={messageContent}
        messageType="info"
        onClose={handleCloseMessage}
      />

      <ErrorDetailModal
        isOpen={showErrorDetailModal}
        errors={errorDetails}
        warnings={warningDetails}
        detailReport={detailReport}
        onClose={handleCloseErrorDetailModal}
      />
    </div>
  );
};

export default UploadExcel;
